// Login completo basado en smart_front - Solo para ADMIN
import React, { useRef, useState, useEffect } from 'react'
import './style.css'
import { encryptAsync } from '../../utils/encrypt/encrypt'
import { LogIn } from '../../graphql/logIn'
import { RecoveryPassword, ChangePassword } from '../../graphql/recoveryPassword'
import { useFormState } from '../../hooks/useForm'
import { LoginForm } from './loginForm'
import { ForgotForm } from './forgotForm'
import { ResetForm } from './resetForm'

// Logo oficial de SmartCFO (mismo que el portal)
const iconUrl = 'https://www.smartcfo.cl/wp-content/uploads/2023/01/Group-138.png'

export default function SignIn({ history, onLogin, location }) {
  const [loading, setLoading] = useState(false)
  const [wrongPass, setWrongPass] = useState(false)
  const [error, setError] = useState(null)
  const [loadingText, setLoadingText] = useState(null)
  const [form, setForm] = useState('login')
  const [token, setToken] = useState(null)
  const [alert, setAlert] = useState(null)

  const forms = useRef({})
  const logIn = LogIn()
  const recoveryPassword = RecoveryPassword()
  const changePassword = ChangePassword()

  // Form states usando el hook de smart_front
  const [formForgotPasswordValues, handleForgotPasswordInputChange] = useFormState({
    emailForgot: '',
  })
  const [formNewPasswordValues, handleNewPasswordInputChange] = useFormState({
    newPassword: '',
    confirmPassword: '',
  })

  const { emailForgot } = formForgotPasswordValues
  const { newPassword, confirmPassword } = formNewPasswordValues

  // Detectar si viene de un link de reset password
  useEffect(() => {
    if (location?.pathname?.startsWith('/reset-password')) {
      setForm('resetPassword')
      const splitToken = location.pathname.split('/')
      if (splitToken.length > 2) {
        setToken(splitToken[2])
      }
    }
  }, [location])

  const showAlert = (message, type = 'info', duration = 5000) => {
    setAlert({ message, type })
    setTimeout(() => setAlert(null), duration)
  }

  const handleSignIn = async () => {
    try {
      if (!process.env.REACT_APP_ENCRYPTED_PASSWORD) {
        setWrongPass(true)
        setError('Falta REACT_APP_ENCRYPTED_PASSWORD en el front')
        return
      }
      setLoading(true)
      setWrongPass(false)
      setError(null)
      const { signInEmailField, signInPasswordField } = forms.current

      const email = signInEmailField?.value?.trim()
      const password = signInPasswordField?.value?.trim()

      if (!email || !password) {
        setWrongPass(true)
        setLoading(false)
        setError('Por favor, ingresa tu correo electrónico y contraseña')
        return
      }

      setLoadingText('Encriptando...')
      const encryptedPassword = await encryptAsync(password)
      setLoadingText('Validando credenciales...')
      const { data, error: loginError } = await logIn({ email, pass: encryptedPassword })

      if (loginError) {
        setLoading(false)
        setWrongPass(true)
        const errorMsg = String(loginError.message).toLowerCase()
        if (errorMsg.includes('admin')) {
          setError('Acceso solo para administradores')
        } else if (errorMsg.includes('unauthorized')) {
          setError('Correo electrónico o contraseña incorrectos')
        } else {
          setError('Credenciales incorrectas, por favor verifica tu correo electrónico y contraseña')
        }
        return
      }

      const accessToken = data?.login?.accessToken
      if (!accessToken) {
        setLoading(false)
        setWrongPass(true)
        setError('Credenciales inválidas')
        return
      }

      localStorage.setItem('token', accessToken)
      localStorage.setItem('userEmail', email)
      setLoading(false)
      if (onLogin) onLogin(accessToken, email)
    } catch (e) {
      setLoading(false)
      setWrongPass(true)
      setError('Error al iniciar sesión')
    }
  }

  const sendNewPassword = async (email) => {
    if (!email || !email.trim()) {
      showAlert('Por favor ingresa tu correo electrónico', 'error')
      return
    }
    
    setLoading(true)
    try {
      const isNewPepper = false
      const result = await recoveryPassword({ email: email.trim(), isNewPepper })
      
      if (result?.data) {
        showAlert('Hemos enviado un correo con las instrucciones para restablecer tu contraseña', 'success')
        setForm('login')
      } else if (result?.error) {
        showAlert('No se encontró el usuario. Por favor verifica tu correo electrónico.', 'error')
      }
    } catch (e) {
      showAlert('Error al enviar el correo de recuperación', 'error')
    }
    setLoading(false)
  }

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      showAlert('Ambas contraseñas deben ser iguales', 'error')
      return
    }
    
    if (newPassword.length < 6) {
      showAlert('La contraseña debe tener al menos 6 caracteres', 'error')
      return
    }

    setLoading(true)
    try {
      const result = await changePassword({ input: { password: newPassword, token } })
      
      if (result?.data) {
        showAlert('¡Tu contraseña ha sido actualizada!', 'success')
        setForm('login')
        if (history) history.push('/login')
      } else if (result?.error) {
        showAlert('No se pudo actualizar la contraseña. El enlace puede haber expirado.', 'error')
      }
    } catch (e) {
      showAlert('Error al actualizar la contraseña', 'error')
    }
    setLoading(false)
  }

  const onKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (form === 'login') {
        await handleSignIn()
      } else if (form === 'resetPassword') {
        await handleResetPassword()
      }
    }
  }

  const renderForm = () => {
    switch (form) {
      case 'forgotPassword':
        return (
          <ForgotForm
            emailForgot={emailForgot}
            handleForgotPasswordInputChange={handleForgotPasswordInputChange}
            sendNewPassword={sendNewPassword}
            setForm={setForm}
            loading={loading}
          />
        )
      case 'resetPassword':
        return (
          <ResetForm
            newPassword={newPassword}
            handleNewPasswordInputChange={handleNewPasswordInputChange}
            confirmPassword={confirmPassword}
            setForm={setForm}
            loading={loading}
            onSubmit={handleResetPassword}
          />
        )
      default:
        return (
          <LoginForm
            forms={forms}
            setForm={setForm}
            handleSignIn={handleSignIn}
            loading={loading}
            loadingText={loadingText}
            wrongPass={wrongPass}
            error={error}
            onKeyDown={onKeyDown}
          />
        )
    }
  }

  return (
    <div className="signin-container">
      <div className="signin-box">
        <div className="signin-logo-container">
          <img src={iconUrl} alt="SmartCFO" className="signin-logo" />
        </div>
        
        {/* Alert message */}
        {alert && (
          <div className={`alert alert-${alert.type}`}>
            {alert.message}
          </div>
        )}
        
        {renderForm()}
      </div>
    </div>
  )
}

